import { t } from '@lingui/core/macro'
import type { ConnectorLookupState } from 'features/knowledgeDocumentViewer/hooks/useConnectorLookup'
import type { ConnectorLookupTarget } from 'features/knowledgeDocumentViewer/utils/connectorLookup'
import styled from 'styled-components'

const Panel = styled.section`
  position: absolute;
  top: 4rem;
  right: 1rem;
  z-index: 20;
  width: min(26rem, calc(100% - 2rem));
  max-height: calc(100% - 5rem);
  overflow-y: auto;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  box-shadow: 0 0.4rem 1.5rem rgba(0, 0, 0, 0.25);
`
const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`
const Choice = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  margin-top: 0.5rem;
  padding: 0.6rem;
  background: transparent;
  color: inherit;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  cursor: pointer;
`

function TargetChoice({
  target,
  onSelect,
}: {
  target: ConnectorLookupTarget
  onSelect: (target: ConnectorLookupTarget) => void
}) {
  const { page_number: page, revision_code: revision } = target
  const select = () => onSelect(target)
  return (
    <Choice onClick={select}>
      <strong>{target.title || target.drawing_number}</strong>
      <div>
        {target.drawing_number} · {t`Page ${page}`}
        {revision ? t` · Rev ${revision}` : ''}
      </div>
      <div>{target.evidence}</div>
    </Choice>
  )
}

export function ConnectorLookupPanel({
  state,
  onSelect,
  onClose,
  onRetry,
}: {
  state: ConnectorLookupState
  onSelect: (target: ConnectorLookupTarget) => void
  onClose: () => void
  onRetry: () => void
}) {
  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()
  return (
    <Panel
      aria-label={t`Connector destination lookup`}
      onClick={stopPropagation}>
      <Heading>
        <strong>{state.label}</strong>
        <button onClick={onClose} aria-label={t`Close destination lookup`}>
          ×
        </button>
      </Heading>
      <div role='status' aria-live='polite'>
        {state.loading
          ? t`Finding destination…`
          : state.error ||
            (state.response?.status === 'not_found'
              ? t`No supported destination found.`
              : t`Choose a destination:`)}
      </div>
      {state.error && <button onClick={onRetry}>{t`Try again`}</button>}
      {state.response?.targets.map((target) => (
        <TargetChoice
          key={target.page_id}
          target={target}
          onSelect={onSelect}
        />
      ))}
    </Panel>
  )
}
